<template>
  <div class="season-pass-task-panel">
    <div v-if="nextLockedSentence" class="task-text next-locked-text">
      {{ nextLockedSentence }}
    </div>

    <div v-else-if="currentTask" class="task-text">
      <div class="text1">
        {{ $t("seasonpass.task.label", { 0: levelNumber }) }}
      </div>
      <div class="text2">
        {{ taskLabel }}
      </div>
    </div>

    <template v-if="ticketsEnabled">
      <div class="ticket-icon"></div>
      <div class="ticket-counter">{{ ticketCount }}</div>
    </template>

    <div
      v-if="isPremium"
      class="premium-button d-inline-flex align-items-center justify-content-around"
    >
      <div class="text1">
        {{ $t("seasonpass.tier.activated", { 0: tierName }) }}
      </div>
      <div class="check-icon"></div>
    </div>
    <div
      v-else
      class="premium-button pointer d-inline-flex align-items-center"
      :class="
        moneyPrice
          ? 'money-price justify-content-center'
          : 'justify-content-between'
      "
      @click="$emit('buy-premium')"
    >
      <div class="text1">{{ premiumLabel }}</div>
      <div v-if="moneyPrice" class="premium-prices">
        <div class="premium-price">
          <span>{{ moneyPrice }}</span>
        </div>
      </div>
      <div v-else-if="premiumPrices.length" class="premium-prices">
        <div
          v-for="price in premiumPrices"
          :key="price.currency"
          class="premium-price"
        >
          <span>{{ formatNumber(price.amount, true) }}</span>
          <img :src="price.icon" :alt="price.currency" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SeasonPassTaskPanel",
  props: {
    pass: {
      type: Object,
      required: true,
    },
    userData: {
      type: Object,
      default: null,
    },
    isPremium: {
      type: Boolean,
      default: false,
    },
    ticketsEnabled: {
      type: Boolean,
      default: false,
    },
    ticketCount: {
      type: Number,
      default: 0,
    },
    currentTask: {
      type: Object,
      default: null,
    },
    nextLockedLevel: {
      type: Object,
      default: null,
    },
    tierName: {
      type: String,
      default: "",
    },
    moneyPrice: {
      type: String,
      default: "",
    },
  },
  computed: {
    locale() {
      return this.$store.state.locale || "de";
    },
    displayConfig() {
      const raw = this.pass.display_config || this.pass.displayConfig;
      if (!raw) return {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (e) {
          return {};
        }
      }
      return raw;
    },
    levelNumber() {
      if (this.currentTask && this.currentTask.level) {
        return this.currentTask.level;
      }

      const level = this.userData && this.userData.level;
      if (level && level.level) {
        return level.level;
      }

      return 1;
    },
    taskLabel() {
      if (!this.currentTask) return "";
      const taskType = this.currentTask.task_type
        ? String(this.currentTask.task_type).split("\\").slice(-1)[0]
        : "";
      const payload = this.currentTask.payload || {};
      const amount = payload.win || payload.levelUps || payload.spins || "";
      // Thousands separators like missions / event system (2,000,000 / 2.000.000).
      return this.$t(`seasonpass.task.type.${taskType}`, {
        0: this.formatNumber(amount),
      });
    },
    premiumTier() {
      const tiers = (this.pass && this.pass.tiers) || [];
      return (
        tiers.find((t) => t.priority === 2) ||
        tiers.find((t) => Number(t.priority) === 2) ||
        null
      );
    },
    premiumLabel() {
      const cta = this.displayConfig.cta_text;
      if (cta && typeof cta === "object") {
        return cta[this.locale] || cta.fallback || cta.en || "";
      }
      if (typeof cta === "string") return cta;
      return this.$t("seasonpass.tier.activate.action", { 0: this.tierName });
    },
    premiumPrices() {
      if (!this.premiumTier) return [];

      const prices = [
        {
          currency: "gold",
          amount: this.premiumTier.gold,
          icon: this.$whitelabel.currencies.primary.single,
        },
        {
          currency: "crowns",
          amount: this.premiumTier.crowns,
          icon: this.$whitelabel.currencies.secondary.single,
        },
      ];

      return prices.filter((price) => Number(price.amount) > 0);
    },
    nextLockedSentence() {
      const lvl = this.nextLockedLevel;
      if (!lvl || !lvl.start_at) return null;
      const startMs = new Date(lvl.start_at).getTime();
      if (startMs <= Date.now()) return null;
      const crowns = Number(lvl.crowns) || 0;
      const gold = Number(lvl.gold) || 0;
      const cost = crowns > 0 ? crowns : gold;
      if (!cost) return null;
      const currency = crowns > 0 ? "crowns" : "gold";
      const d = new Date(lvl.start_at);
      const day = d.getDate();
      const monthName = this.$t(`month_${d.getMonth()}`);
      const dateStr =
        this.locale === "en" ? `${monthName} ${day}` : `${day}. ${monthName}`;
      return this.$t("seasonpass.level.unlock.label", {
        0: dateStr,
        1: this.formatNumber(cost),
        2: this.$t(`reward.${currency}`),
      });
    },
  },
};
</script>

<style scoped lang="scss">
.season-pass-task-panel {
  position: absolute;
  top: 28px;
  right: 100px;
  z-index: 20;
  width: 513px;
  height: 173px;
  pointer-events: none;
  font-family: kanit-semibold;
  text-transform: uppercase;
  color: var(--sp-text-color, white);

  .pointer {
    cursor: pointer;
    pointer-events: auto;
  }

  .task-text {
    position: absolute;
    left: 50%;
    top: 30px;
    transform: translateX(-50%);
    width: 400px;
    text-align: center;

    .text1 {
      font-size: 18px;
      color: var(--sp-text-color, white);
    }
    .text2 {
      font-size: 26px;
      line-height: 1.1;
      margin-top: 2px;
      color: var(--sp-highlight-color, white);
    }
  }

  .ticket-icon {
    position: absolute;
    top: 28px;
    right: 112px;
    width: 47px;
    height: 47px;
    background: url(~@/assets/img/events/winter-bonus-jagd/icn-reward-ticket.png)
      center/100% 100% no-repeat;
    filter: drop-shadow(0 0 7px $drop-shadow-dark)
      drop-shadow(0 0 3px $drop-shadow-dark);
  }

  .ticket-counter {
    position: absolute;
    top: 31px;
    right: 54px;
    width: 49px;
    font-size: 26px;
    text-align: center;
  }

  .premium-button {
    position: absolute;
    top: 126px;
    left: 50%;
    transform: translateX(-50%);
    width: 360px;
    height: 50px;
    padding-left: 10px;
    padding-right: 10px;
    background: transparent;

    .text1 {
      font-size: 18px;
      text-shadow: 0 0 4px $drop-shadow-dark;
      line-height: 1;
      flex: 1;
      text-align: center;
    }

    .premium-prices {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
      flex-shrink: 0;
      margin-left: 8px;
    }

    // Real money: keep label and price together in the center.
    &.money-price .text1 {
      flex: 0 1 auto;
    }

    .premium-price {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 20px;
      line-height: 1;
      text-shadow: 0 0 4px $drop-shadow-dark;
      white-space: nowrap;

      img {
        width: 28px;
        height: 28px;
        object-fit: contain;
        filter: drop-shadow(0 0 3px $drop-shadow-dark);
      }
    }

    .check-icon {
      position: absolute;
      right: 10px;
      top: 0;
      width: 48px;
      height: 48px;
      background: url(~@/assets/img/events/winter-bonus-jagd/green-checkmark-circle.png)
        right/100% 100% no-repeat;
    }
  }
}
</style>
